import { Box, Tooltip } from "@chakra-ui/react";
import { TableData } from "../../../../components/Table/types";
import { DstSchema, SrcSchema, SrcSchemaItem } from "../../../../api/types";
import { typedSxMap } from "../../../../utils/typedSxMap";
import { FaCircleCheck, FaRegCircle } from "react-icons/fa6";

const styles = typedSxMap({
  examples: {
    display: "flex",
    gap: "var(--m-xs)",
    fontFamily: "monospace",
    overflow: "hidden",
  },
  exampleItem: {
    backgroundColor: "var(--color-input-background)",
    padding: "0 var(--m-xxxs)",
    borderRadius: "var(--border-radius-2)",
    textWrap: "nowrap",
  },
  tooltip: {
    position: "absolute",
    backgroundColor: "var(--color-background-modal)",
    padding: "var(--m-xxs) var(--m-xs)",
    borderRadius: "var(--border-radius)",
    marginTop: "var(--m-xs)",
    boxShadow: "0 0 0 1px var(--color-border), 0 5px 15px rgba(0, 0, 0, 0.2)",
    color: "var(--color-text)",
  },
});

export function overviewTable(dstSchema: DstSchema, srcSchema: SrcSchema): TableData {
  const srcSchemaAlreadyChecked: string[] = [];

  const dstSchemaList = dstSchema.map((schema, index) => {
    const srcSchemaColumn = srcSchema[schema.mapping_result?.rule.source_columns?.[0]];
    srcSchemaColumn && srcSchemaAlreadyChecked.push(srcSchemaColumn.name || "");
    const hasMapping = schema.mapping_result?.rule.source_columns?.[0] || schema.mapping_result?.rule.source_columns?.[0] === 0;
    return {
      id: dstSchema?.[0].key + index + schema.name + schema.mapping_result,
      "": {
        raw: schema.name,
        content: (
          <Box>
            {hasMapping ? (
              <FaCircleCheck color="green" size={18} />
            ) : schema.default_value ? (
              <FaCircleCheck color="gray" size={18} />
            ) : (
              <FaRegCircle color="gray" size={18} />
            )}
          </Box>
        ),
      },
      "Destination Column": {
        raw: schema.name,
        content: <Box>{schema.name}</Box>,
      },
      "Source Column": {
        raw: schema.mapping_result,
        content: <Box>{srcSchemaColumn?.name}</Box>,
      },
      "Rows with Data": {
        raw: schema.mapping_result,
        content: <Box>{srcSchemaColumn?.num_not_blank_cells}</Box>,
      },
      "Sample Data": {
        raw: schema.mapping_result,
        content: (
          <Tooltip
            sx={styles.tooltip}
            label={
              <ul>
                {srcSchemaColumn?.sample_data?.map((sample: string, index) => {
                  return <li key={schema.mapping_result + sample + index}>{sample}</li>;
                })}
              </ul>
            }
            aria-label={srcSchemaColumn?.sample_data?.join(", ") || ""}>
            <Box sx={styles.examples}>
              {srcSchemaColumn?.sample_data?.map((value, index) => (
                <Box key={schema.mapping_result + value + index} sx={styles.exampleItem}>
                  {value}
                </Box>
              ))}
            </Box>
          </Tooltip>
        ),
      },
    };
  });

  const srcSchemaList = srcSchema
    .filter((schema: SrcSchemaItem) => !srcSchemaAlreadyChecked.includes(schema.name))
    .filter((schema) => schema.num_not_blank_cells > 0)
    .filter((schema) => schema.name && schema.name.trim() !== "")
    .map((schema, index) => {
      return {
        id: index + schema.name,
        "": {
          raw: schema.name,
          content: (
            <Box>
              <FaRegCircle color="gray" size={18} />
            </Box>
          ),
        },
        "Destination Column": {
          raw: "none",
          content: <Box></Box>,
        },
        "Source Column": {
          raw: schema.name,
          content: <Box>{schema.name}</Box>,
        },
        "Sample Data": {
          raw: "",
          content: <Box></Box>,
        },
      };
    });

  return [...dstSchemaList, ...srcSchemaList];
}
